import Breadcrumb from 'components/Layout/ApplicationLayout/Breadcrumb';
import useErrorNetworkMessage from 'hook/useErrorNetworkMessage';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useMultiLanguageVisitedLog from 'page/Application/VisitedPage/hook/useMultiLanguageVisitedLog';
import HeaderListDataLayout from 'page/Application/VisitedPage/component/HeaderListDataLayout';
import LayoutDefault from 'page/Application/VisitedPage/component/LayoutDefault';
import { ButtonTextIcon, LineVertical } from 'components';
import { DATE_FORMAT_LIST } from 'constant/notificationConstants';
import { ButtonOkStyle } from 'page/Application/ManageCompanyList/component/Form/styled';
import {
  FormDropDownGroup,
  Grid1,
  Grid2,
  TitleStyled,
} from 'page/Application/VisitedPage/component/HeaderListDataLayout/styled';
import FormSelectSearch from 'page/Application/ManageCompanyList/CompanyList/component/FormSelectSearch';
import { RedoOutlined } from '@ant-design/icons';
import {
  defaultPageSizeOptions,
  defaultSelectPageSizeOption,
} from 'constant/Pagination/pageSizeOption';
import FormDatePicker from 'page/Application/VisitedPage/component/DatePicker';
import Pagination from 'components/Pagination';
import {
  TableGroup,
  TableStyle,
} from 'page/Application/VisitedPage/component/LayoutDefault/styled';
import useSetTitlePage from 'hook/useSetTitlePage';
import TableData from 'page/Application/VisitedPage/component/TablePageAccess';
import useCustomQuery from 'hook/useQuery/useCustomQuery';
import { Form, Spin } from 'antd';
import {
  getListProductLog,
  getListVisited,
  getListVisitedAccounts,
  getListVisitedPartners,
} from 'api/log/visitedPageLog.api';
import dayjs from 'dayjs';

const VisitedPage = () => {
  const timeZoneJapan = 'Asia/Tokyo';
  const defaultFromDate = dayjs().subtract(30, 'day');
  const defaultToDate = dayjs();

  const { useQueryAPI } = useCustomQuery();
  const [form] = Form.useForm();
  const { isOnline, errorNetWork, contextErrorNetWork } =
    useErrorNetworkMessage();
  const [page, setPage] = useState(10);
  const [current, setCurrent] = useState(1);
  const [currentPartner, setCurrentPartner] = useState(1);
  const [currentAccount, setCurrentAccount] = useState(1);
  const [productId, setProductId] = useState('');
  const [dateRange, setDateRange] = useState({ dateFrom: '', dateTo: '' });
  const [resetTrigger, setResetTrigger] = useState(0);
  const { VISITED_LOG_LIST_TITLE } = useMultiLanguageVisitedLog();
  const [emptyText, setEmptyText] = useState('データがありません');

  const listItem = useMemo(() => {
    return [
      {
        title: <Link to={'/'}>企業管理</Link>,
      },
      {
        title: VISITED_LOG_LIST_TITLE,
      },
    ];
  }, []);

  const { data: dataProductList, isLoading } = useQueryAPI({
    api: getListProductLog,
    params: {
      keyword: '',
    },
    key: 'getListProductLog',
  });

  const productList = useMemo(() => {
    if (
      dataProductList &&
      dataProductList?.data &&
      dataProductList?.ec === false
    ) {
      return dataProductList?.data?.map((item) => ({
        ...item,
        key: item.productId,
        label: item.productName,
        value: item.productId,
      }));
    } else return [];
  }, [dataProductList]);

  const { data: dataListVisited = [], isLoading: isLoadingGetListVisited } =
    useQueryAPI({
      api: getListVisited,
      key: 'getListVisited',
      params: {
        pageNumber: current,
        pageSize: page,
        dateFrom: dateRange?.dateFrom
          ? dateRange?.dateFrom
          : defaultFromDate?.format(DATE_FORMAT_LIST[3]),
        dateTo: dateRange?.dateTo
          ? dateRange?.dateTo
          : defaultToDate?.format(DATE_FORMAT_LIST[3]),
        timeZoneId: timeZoneJapan,
        productId: productId,
      },
    });

  const {
    data: dataListVisitedAccounts = [],
    isLoading: isLoadingGetListVisitedAccounts,
  } = useQueryAPI({
    api: getListVisitedAccounts,
    key: 'getListVisitedAccounts',
    params: {
      pageNumber: currentAccount,
      pageSize: page,
      dateFrom: dateRange?.dateFrom
        ? dateRange?.dateFrom
        : defaultFromDate?.format(DATE_FORMAT_LIST[3]),
      dateTo: dateRange?.dateTo
        ? dateRange?.dateTo
        : defaultToDate?.format(DATE_FORMAT_LIST[3]),
      timeZoneId: timeZoneJapan,
      productId: productId,
    },
  });

  const {
    data: dataListVisitedPartners = [],
    isLoading: isLoadingGetListVisitedPartners,
  } = useQueryAPI({
    api: getListVisitedPartners,
    key: 'getListVisitedPartners',
    params: {
      pageNumber: currentPartner,
      pageSize: page,
      dateFrom: dateRange?.dateFrom
        ? dateRange?.dateFrom
        : defaultFromDate?.format(DATE_FORMAT_LIST[3]),
      dateTo: dateRange?.dateTo
        ? dateRange?.dateTo
        : defaultToDate?.format(DATE_FORMAT_LIST[3]),
      timeZoneId: timeZoneJapan,
      productId: productId,
    },
  });

  const isLoadingAllPage = useMemo(() => {
    return (
      isLoadingGetListVisited &&
      isLoadingGetListVisitedPartners &&
      isLoadingGetListVisitedAccounts
    );
  }, [
    isLoadingGetListVisited,
    isLoadingGetListVisitedAccounts,
    isLoadingGetListVisitedPartners,
  ]);

  const handleChangePageSize = (value) => {
    if (isOnline) {
      setPage(value);
      setCurrent(1);
      setCurrentPartner(1);
      setCurrentAccount(1);
    } else {
      errorNetWork();
    }
  };

  const onFinish = () => {
    if (isOnline) {
      const values = form.getFieldsValue();
      const selectedDates = values?.publishedFromToDate;

      if (selectedDates && selectedDates.length === 2) {
        setDateRange({
          dateFrom: selectedDates[0]
            ? selectedDates[0].format(DATE_FORMAT_LIST[3])
            : '',
          dateTo: selectedDates[1]
            ? selectedDates[1].format(DATE_FORMAT_LIST[3])
            : '',
        });
      } else {
        setDateRange({ dateFrom: '', dateTo: '' });
      }

      setProductId(values?.productId ?? '');
      setCurrent(1);
      setCurrentPartner(1);
      setCurrentAccount(1);
      setEmptyText('データが見つかりませんでした。');
    } else {
      errorNetWork();
    }
  };

  const handleResetData = () => {
    if (isOnline) {
      setPage(10);
      setCurrent(1);
      setCurrentPartner(1);
      setCurrentAccount(1);
      setProductId('');
      form.resetFields(['productId']);
      form.setFieldValue('publishedFromToDate', [
        defaultFromDate,
        defaultToDate,
      ]);
      setDateRange({ dateFrom: '', dateTo: '' });
      setResetTrigger((prev) => prev + 1);
      setEmptyText('データがありません');
    } else {
      errorNetWork();
    }
  };

  const handlePagingChange = (pageNumber, pageSize) => {
    if (isOnline) {
      setCurrent(pageNumber);
      setPage(pageSize);
    } else {
      errorNetWork();
    }
  };

  const handlePagingChangePartner = (pageNumber, pageSize) => {
    if (isOnline) {
      setCurrentPartner(pageNumber);
      setPage(pageSize);
    } else {
      errorNetWork();
    }
  };

  const handlePagingChangeAccount = (pageNumber, pageSize) => {
    if (isOnline) {
      setCurrentAccount(pageNumber);
      setPage(pageSize);
    } else {
      errorNetWork();
    }
  };

  useSetTitlePage('ページ | Soliton パートナーサイト', true);

  return (
    <>
      {contextErrorNetWork}
      <Breadcrumb listItem={listItem} titlePage={VISITED_LOG_LIST_TITLE} />
      <LayoutDefault>
        <Form form={form}>
          <HeaderListDataLayout
            titleScreen={VISITED_LOG_LIST_TITLE}
            value={page}
            handleChangePageSize={handleChangePageSize}
            pageSizeOptions={defaultSelectPageSizeOption}
          >
            <>
              <ButtonTextIcon
                text={'リフレッシュ'}
                icon={<RedoOutlined />}
                onClick={handleResetData}
              />
              <LineVertical />
              <FormDropDownGroup>
                <Grid1>
                  <TitleStyled>プロダクト名</TitleStyled>
                </Grid1>
                <Grid2>
                  <FormSelectSearch
                    placeholder={'プロダクト名を入力してください。'}
                    name={'productId'}
                    height={'36px'}
                    data={productList}
                    isLoading={isLoading}
                    allowClear={false}
                  />
                </Grid2>
              </FormDropDownGroup>
              <LineVertical />
              <FormDatePicker
                form={form}
                name="publishedFromToDate"
                width={'360px'}
                height={'36px'}
                dateFormat={DATE_FORMAT_LIST[1]}
                isShowTime={false}
                required={false}
                defaultFromDate={defaultFromDate}
                defaultToDate={defaultToDate}
                resetTrigger={resetTrigger}
              />
              <LineVertical />
              <ButtonOkStyle
                type="primary"
                htmlType="submit"
                onClick={onFinish}
              >
                {' '}
                検索
              </ButtonOkStyle>
            </>
          </HeaderListDataLayout>
        </Form>
        <Spin
          spinning={
            isLoadingGetListVisited &&
            isLoadingGetListVisitedPartners &&
            isLoadingGetListVisitedAccounts
          }
        >
          <TableGroup>
            <TableStyle>
              <TableData
                isLoading={isLoadingAllPage ? false : isLoadingGetListVisited}
                dataSource={dataListVisited?.data?.list}
                AccessTableType={'ACCESS_BY_PAGE'}
                emptyText={isLoadingGetListVisited ? '集計中です。' : emptyText}
              />
              <Pagination
                currentPage={current}
                isShowTotal={false}
                isShowChangePageSize={false}
                handlePagingChange={handlePagingChange}
                total={dataListVisited?.data?.total}
                pageSize={page}
                pageSizeOptions={defaultPageSizeOptions}
                handleChangePageSize={handleChangePageSize}
              />
            </TableStyle>
            <TableStyle>
              <TableData
                isLoading={
                  isLoadingAllPage ? false : isLoadingGetListVisitedPartners
                }
                dataSource={dataListVisitedPartners?.data?.list}
                AccessTableType={'ACCESS_BY_PARTNER'}
                emptyText={
                  isLoadingGetListVisitedPartners ? '集計中です。' : emptyText
                }
              />
              <Pagination
                currentPage={currentPartner}
                isShowTotal={false}
                isShowChangePageSize={false}
                handlePagingChange={handlePagingChangePartner}
                total={dataListVisitedPartners?.data?.total}
                pageSize={page}
                pageSizeOptions={defaultPageSizeOptions}
                handleChangePageSize={handleChangePageSize}
              />
            </TableStyle>
            <TableStyle>
              <TableData
                isLoading={
                  isLoadingAllPage ? false : isLoadingGetListVisitedAccounts
                }
                dataSource={dataListVisitedAccounts?.data?.list}
                AccessTableType={'ACCESS_BY_ACCOUNT'}
                emptyText={
                  isLoadingGetListVisitedAccounts ? '集計中です。' : emptyText
                }
              />
              <Pagination
                currentPage={currentAccount}
                isShowTotal={false}
                isShowChangePageSize={false}
                handlePagingChange={handlePagingChangeAccount}
                total={dataListVisitedAccounts?.data?.total}
                pageSize={page}
                pageSizeOptions={defaultPageSizeOptions}
                handleChangePageSize={handleChangePageSize}
              />
            </TableStyle>
          </TableGroup>
        </Spin>
        <Pagination
          totalTitle={'トラッキングコード総数'}
          pageSize={page}
          isShowTotal={false}
          isShowPagination={false}
          pageSizeOptions={defaultPageSizeOptions}
          handleChangePageSize={handleChangePageSize}
        />
      </LayoutDefault>
    </>
  );
};

export default VisitedPage;
